import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import {
  contractSetupDeleteFieldsOverMaxDocumentPage,
  contractSetupParticipantRemove,
} from '@app/store/contract-setup/actions';
import FieldNode from '@app/store/FieldNode';
import FieldType from '@app/store/FieldType';
import {
  templateDeleteFieldsOverMaxDocumentPage,
  templateParticipantDelete,
} from '@app/store/template/actions';

import {
  initializeDocumentRenderTree,
  insertNodeToFieldNode,
  insertNodeToPageRootInDocumentRenderTree,
  removeNodeFromDocumentRenderTree,
  setDocumentRenderTree,
} from './actions';

/*
  서명자필드에 대한 렌더링 트리입니다.

  필드노드는 아래와 같이 정의됩니다.
  TODO(@DevSDK): 필드 노드 소스코드 url 첨부

  서명자필드 렌더 트리는 다음의 계층 구조를 가집니다.
  최상위루트
  └── 페이지 루트
        └── 필드

  e.g.
    note: Type(id)

    Root(roots)
    ├── Root(root-1)
    │   ├── Signature
    │   ├── Signature
    │   └── Checkbox
    ├── Root(root-4)
    │   ├── Text
    │   ├── Signature
    │   └── Checkbox
    ├── Root(root-6)
    │   └── Text
    └── Root(root-12)
        └── Checkbox

    렌더트리는 사용하기 전 initializeDocumentRenderTree 혹은 setDocumentRenderTree을 통해
    초기화 후 활용되어야 합니다.

    actions:
      setDocumentRenderTree에서 소비되는 RootInPage은 페이지에 속한 노드들을 의미합니다.
      insertNodeToPageRootInDocumentRenderTree는 페이지 루트에 노드를 추가합니다. 만약 페이지루트가 없다면 페이지루트를 추가합니다.
      removeNodeFromDocumentRenderTree는 렌더트리로부터 id와 매칭되는 노드를 제거합니다. 만약 제거한 후 페이지루트가 비었다면 페이지 루트도 제거합니다.
      insertNodeToFieldNode는 특정 id노드에 자식을 추가합니다.
*/

export interface RootInPage {
  [page: number]: FieldNode[];
}

export const findNode = (id: string, node: FieldNode): FieldNode | null => {
  if (id === node.id) return node;
  if (!node.children) return null;

  // eslint-disable-next-line no-restricted-syntax
  for (const child of node.children) {
    const result = findNode(id, child);
    if (result) return result;
  }
  return null;
};

const removeNodes = (ids: string[], root: FieldNode) => {
  ids.forEach((id) => {
    const node = findNode(id, root);
    if (!node) return;
    const parentNode = findNode(node.parentId as string, root);
    if (!parentNode?.children) {
      // TODO(@DevSDK): find pure way without throw new Error('a render tree element does not have parentNode or parentId');
      return;
    }
    const index = parentNode?.children.indexOf(node);
    if (index > -1) parentNode.children.splice(index, 1);
    if (parentNode.id.startsWith('root-') && parentNode.children.length === 0) {
      root.children = root.children?.filter((child) => child.id !== parentNode.id);
    }
  });
};

export const setParentIdRecursively = (node: FieldNode) => {
  if (!node.children) return;

  node.children.forEach((child) => {
    child.parentId = node.id;
    setParentIdRecursively(child);
  });
};

const renderTreeSlice = createSlice({
  name: 'document/renderTree',
  initialState: {
    id: 'roots',
    type: FieldType.ROOT,
    children: [],
  } as FieldNode,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(initializeDocumentRenderTree, (state) => {
      state.children = [];
    });
    builder.addCase(setDocumentRenderTree, (state, action) => {
      const roots = Object.keys(action.payload).map((key) => {
        return {
          id: `root-${key}`,
          type: FieldType.ROOT,
          children: action.payload[+key],
        } as FieldNode;
      });
      state.children = roots;
      setParentIdRecursively(state);
    });
    builder.addCase(insertNodeToFieldNode, (state, action) => {
      const node = findNode(action.payload.targetId, state as FieldNode);
      if (!node) return;
      if (!node.children) node.children = [];
      action.payload.node.parentId = node.id;
      node.children?.push(action.payload.node);
    });
    builder.addCase(insertNodeToPageRootInDocumentRenderTree, (state, action) => {
      const { page, node } = action.payload;
      setParentIdRecursively(node);
      const filtered = state.children?.filter((node) => node.id === `root-${page}`);
      if (!filtered || filtered?.length === 0) {
        const root = state as FieldNode;
        root.children?.push({
          id: `root-${page}`,
          type: FieldType.ROOT,
          parentId: 'roots',
          children: [{ ...action.payload.node, parentId: `root-${page}` }],
        });
      } else {
        action.payload.node.parentId = `root-${page}`;
        filtered[0].children?.push(action.payload.node);
      }
    });
    builder.addCase(removeNodeFromDocumentRenderTree, (state, action) => {
      removeNodes([action.payload], state as FieldNode);
    });
    builder.addCase(contractSetupParticipantRemove, (state, action) => {
      removeNodes(action.payload.participantFieldIds, state as FieldNode);
    });
    builder.addCase(contractSetupDeleteFieldsOverMaxDocumentPage, (state, action) => {
      removeNodes(action.payload.fieldIds, state as FieldNode);
    });
    builder.addMatcher(
      isAnyOf(templateParticipantDelete, templateDeleteFieldsOverMaxDocumentPage),
      (state, action) => {
        removeNodes(action.payload.fieldIds, state as FieldNode);
      }
    );
  },
});

export const { reducer } = renderTreeSlice;
